import { Box, Group, Image, rem, Select, UnstyledButton } from '@mantine/core'

import {
  BOGGLE_RUN_SUMMARY_RATINGS,
  type BoggleRunSummaryRatingId,
} from '~/boggle/config/runSummaryScore'
import { BOGGLE_RATING_BADGE_IMAGES } from './ratingAssets'
import { RatingBadge } from './RatingBadge'

export type RatingFilterValue = 'all' | BoggleRunSummaryRatingId

const RATING_LABELS = Object.fromEntries(
  BOGGLE_RUN_SUMMARY_RATINGS.map(rating => [rating.id, rating.label]),
) as Record<BoggleRunSummaryRatingId, string>

const CHIP_SIZE_PX = 48
const CHIP_ICON_PX = 34

export type RatingFilterVariant = 'chips' | 'select'

const RATING_SELECT_ICON_PX = 26

function RatingFilterSelectOptionRow({
  id,
  label,
}: {
  id: RatingFilterValue
  label: string
}) {
  return (
    <Group gap={6} wrap="nowrap" h={20} fz={16}>
      {id !== 'all' ? (
        <RatingBadge h={RATING_SELECT_ICON_PX} ratingId={id} w={RATING_SELECT_ICON_PX} />
      ) : null}
      {label}
    </Group>
  )
}

/**
 * Shared visual language for the compact rating-chip rows used by Boggle UI.
 * Hosts decide which rating ids belong in the row; this component owns only
 * the control grammar and accessibility shape.
 */
export function RatingFilter({
  ariaLabelPrefix = 'Filter by',
  onChange,
  testIdPrefix,
  value,
  values,
  variant = 'chips',
}: {
  ariaLabelPrefix?: string
  onChange: (value: RatingFilterValue) => void
  testIdPrefix?: string
  value: RatingFilterValue
  values: readonly RatingFilterValue[]
  variant?: RatingFilterVariant
}) {
  if (variant === 'select') {
    const data = values.map(id => ({
      label: id === 'all' ? 'All ratings' : RATING_LABELS[id],
      value: id,
    }))
    const showSelectedBadge = value !== 'all'
    return (
      <Select<RatingFilterValue>
        allowDeselect={false}
        aria-label={`${ariaLabelPrefix} rating`}
        data={data}
        data-testid={testIdPrefix}
        styles={{
          input: {
            fontSize: value === 'all' ? rem(14) : rem(16),
            background: 'rgba(0,0,0,0.3)',
            paddingRight: 0,
            border: 'none',
            borderRadius: 999,
          },
        }}
        leftSection={
          showSelectedBadge ? (
            <Box pl={5} pr={4} pt={2}>
              <RatingBadge
                h={RATING_SELECT_ICON_PX}
                ratingId={value}
                w={RATING_SELECT_ICON_PX}
              />
            </Box>
          ) : null
        }
        leftSectionPointerEvents="none"
        leftSectionWidth={showSelectedBadge ? RATING_SELECT_ICON_PX + 16 : undefined}
        onChange={next => onChange((next ?? 'all') as RatingFilterValue)}
        renderOption={({ option }) => (
          <RatingFilterSelectOptionRow
            id={option.value as RatingFilterValue}
            label={option.label}
          />
        )}
        value={value}
      />
    )
  }

  return (
    <Group align="center" gap={10} justify="center" wrap="wrap">
      {values.map(id => {
        const label = id === 'all' ? 'All' : RATING_LABELS[id]
        return (
          <RatingFilterChip
            active={value === id}
            ariaLabel={
              id === 'all'
                ? `${ariaLabelPrefix} all ratings`
                : `${ariaLabelPrefix} ${label}`
            }
            id={id}
            key={id}
            label={label}
            onSelect={() => onChange(id)}
            testIdPrefix={testIdPrefix}
          />
        )
      })}
    </Group>
  )
}

function RatingFilterChip({
  active,
  ariaLabel,
  id,
  label,
  onSelect,
  testIdPrefix,
}: {
  active: boolean
  ariaLabel: string
  id: RatingFilterValue
  label: string
  onSelect: () => void
  testIdPrefix?: string
}) {
  const iconSrc = id === 'all' ? null : (BOGGLE_RATING_BADGE_IMAGES[id] ?? null)

  return (
    <UnstyledButton
      aria-label={ariaLabel}
      aria-pressed={active}
      bd={active ? '1px solid rgba(255,255,255,0.55)' : undefined}
      bdrs={10}
      bg={active ? 'rgba(255,255,255,0.14)' : 'transparent'}
      c={active ? 'white' : 'rgba(232, 224, 210, 0.72)'}
      data-active={active ? 'true' : 'false'}
      data-testid={testIdPrefix ? `${testIdPrefix}-${id}` : undefined}
      h={CHIP_SIZE_PX}
      fz={13}
      onClick={onSelect}
      style={{
        alignItems: 'center',
        display: 'inline-flex',
        flexShrink: 0,
        fontFamily: 'Tilt Neon, sans-serif',
        fontWeight: 600,
        justifyContent: 'center',
        letterSpacing: 1,
        lineHeight: 1,
        textTransform: 'uppercase',
      }}
      w={CHIP_SIZE_PX}
    >
      {iconSrc ? (
        <Image
          alt=""
          h={CHIP_ICON_PX}
          src={iconSrc}
          style={{
            display: 'block',
            objectFit: 'contain',
            opacity: active ? 1 : 0.7,
          }}
          width={CHIP_ICON_PX}
        />
      ) : (
        label
      )}
    </UnstyledButton>
  )
}
